import { ErrorMessageSection } from "./shared";

export function ReviewContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">What Is Sugargoo? A Complete Overview</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Sugargoo is a China-based shopping agent that enables international customers to purchase products from Chinese e-commerce platforms. Acting as an intermediary, Sugargoo handles product sourcing, quality inspection, warehouse storage, and international shipping — bridging the gap between overseas buyers and Chinese sellers who may not offer direct global delivery.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Founded several years ago, Sugargoo has grown into one of the most recognized names in the shopping agent industry. The platform supports purchases from major Chinese marketplaces and provides a streamlined interface that simplifies what would otherwise be a complex cross-border shopping process.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          For shoppers looking to access the vast product ecosystem of Chinese e-commerce — including fashion, sneakers, electronics, accessories, and lifestyle goods — Sugargoo offers a reliable gateway with competitive fees and comprehensive service options. When paired with a well-organized <strong>Sugargoo Spreadsheet</strong>, the product discovery and purchasing process becomes remarkably efficient.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Sugargoo Pros and Cons — Honest Assessment</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="p-5 rounded-xl bg-accent-50 border border-accent-200">
            <h3 className="font-heading text-lg font-semibold text-accent-800 mb-3 flex items-center gap-2">
              <i className="ri-thumb-up-line text-accent-500"></i> Advantages
            </h3>
            <ul className="space-y-2">
              {[
                "Vast product catalog spanning fashion, sneakers, electronics, and lifestyle categories",
                "Competitive agent fees compared to other major shopping platforms in the same space",
                "Reliable quality inspection photos before international dispatch for every order",
                "Multiple shipping methods including EMS, DHL, and economical sea freight options",
                "User-friendly interface with detailed order tracking and account management tools",
                "Active community ecosystem sharing hauls, reviews, and Sugargoo Spreadsheet resources",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-200">
            <h3 className="font-heading text-lg font-semibold text-secondary-800 mb-3 flex items-center gap-2">
              <i className="ri-information-line text-secondary-500"></i> Considerations
            </h3>
            <ul className="space-y-2">
              {[
                "International shipping costs can be significant — always estimate before ordering large hauls",
                "Delivery times vary widely; sea shipping takes over a month while express options cost more",
                "Product quality may differ from listing photos — quality inspection is essential before shipping",
                "Some sellers enforce minimum order quantities that may not suit casual or first-time shoppers",
                "Currency conversion fees and payment processing charges may apply depending on your method",
                "Returns and refunds involve navigating seller-specific policies which require patience and documentation",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-information-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
          <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-3">Final Verdict — Is Sugargoo Worth It?</h3>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            Sugargoo earns a strong recommendation for both newcomers and experienced international shoppers in 2026. Its combination of competitive pricing, reliable quality inspection, flexible shipping options, and an engaged community makes it one of the best shopping agents available. We recommend starting with a small test order of 2-3 items to familiarize yourself with the platform flow, then scaling up once you are comfortable with the quality inspection and shipping selection process.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Sugargoo vs Other Shopping Agents — Quick Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-background-300">
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Feature</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-700 bg-primary-50 rounded-t-lg">Sugargoo</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Other Agents</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Agent Fees", sugargoo: "Competitive, transparent", others: "Varies — some higher, some lower" },
                { feature: "QC Photos", sugargoo: "Reliable, multiple angles", others: "Available but quality varies" },
                { feature: "Shipping Options", sugargoo: "EMS, DHL, Air, Sea", others: "Similar range across platforms" },
                { feature: "User Interface", sugargoo: "Clean, intuitive design", others: "Some platforms feel dated" },
                { feature: "Community", sugargoo: "Active, helpful ecosystem", others: "Varies significantly by platform" },
                { feature: "Product Range", sugargoo: "Extensive coverage", others: "Comparable but may differ by niche" },
              ].map((row) => (
                <tr key={row.feature} className="border-b border-background-200/50">
                  <td className="py-3 px-4 font-medium text-foreground-800">{row.feature}</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/50 font-medium">{row.sugargoo}</td>
                  <td className="py-3 px-4 text-foreground-500">{row.others}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ErrorMessageSection />
    </>
  );
}